/**
 * As rotas das planilhas das escolas — spec 0111 (contracts/README.md).
 *
 * Ver é `api:transportador:read`; decidir, cadastrar, trocar o link e enviar
 * arquivo, `api:transportador:write`. O erro do servidor chega como `{ error }`
 * e vira a mensagem de `Error`, que a janela mostra dentro de si.
 */
import { authFetch } from '../../../../services/api';

export type TipoDePendencia = 'planilha' | 'mesmo_aluno' | 'incluir' | 'inativar' | 'reativar' | 'atualizar_campo' | 'turma';
export type Papel = 'sucesso' | 'atencao' | 'perigo' | 'info' | 'neutro';

/** Da mais urgente para a menos (spec §4): a ordem da fila e dos recortes. */
export const ORDEM_DOS_TIPOS: TipoDePendencia[] = ['planilha', 'mesmo_aluno', 'incluir', 'reativar', 'inativar', 'atualizar_campo', 'turma'];

export const TIPO: Record<TipoDePendencia, { rotulo: string; papel: Papel }> = {
  planilha: { rotulo: 'Planilha', papel: 'perigo' },
  mesmo_aluno: { rotulo: 'Mesmo aluno?', papel: 'info' },
  incluir: { rotulo: 'Incluir', papel: 'sucesso' },
  reativar: { rotulo: 'Reativar', papel: 'atencao' },
  inativar: { rotulo: 'Inativar', papel: 'perigo' },
  atualizar_campo: { rotulo: 'Atualizar campo', papel: 'neutro' },
  turma: { rotulo: 'Turma', papel: 'neutro' },
};

/** As colunas do aluno, como quem opera as chama. */
export const ROTULO_DA_COLUNA: Record<string, string> = {
  endAluno: 'Endereço', numAluno: 'Número', compAluno: 'Complemento', bairroAluno: 'Bairro', cepAluno: 'CEP',
  cidadeAluno: 'Município', telResponsavel: 'Telefone da família', nomeResponsavel: 'Responsável', domTurno: 'Turno',
  idTurma: 'Turma', nomeAluno: 'Nome', domSitAluno: 'Situação', aluno: 'Aluno',
};

/** Os campos que o mapeamento liga a uma coluna da planilha. */
export const CAMPOS_DO_MAPEAMENTO: Array<{ valor: string; rotulo: string }> = [
  { valor: 'nomeAluno', rotulo: 'Nome do aluno' }, { valor: 'transportador', rotulo: 'Transportador' },
  { valor: 'endereco', rotulo: 'Endereço' }, { valor: 'numero', rotulo: 'Número' }, { valor: 'bairro', rotulo: 'Bairro' },
  { valor: 'cep', rotulo: 'CEP' }, { valor: 'municipio', rotulo: 'Município' }, { valor: 'turma', rotulo: 'Turma' },
  { valor: 'turno', rotulo: 'Turno' }, { valor: 'telefone', rotulo: 'Telefone da família' },
  { valor: 'responsavel', rotulo: 'Responsável' }, { valor: 'observacao', rotulo: 'Observação' }, { valor: 'simade', rotulo: 'SIMADE' },
];

export interface AtoDaPlanilha {
  ato: 'trocar_link' | 'pausar' | 'retomar' | 'retirar' | 'fixar_aba';
  de: string | null;
  para: string | null;
  por: string;
  em: string;
}

export interface Planilha {
  id: number;
  idEscola: number;
  escola: string;
  sigla: string;
  titulo: string;
  tipoArquivo: 'nativa' | 'xlsx';
  situacao: 'ativa' | 'pausada' | 'retirada';
  abaFixada: string | null;
  ultimaAba: string | null;
  ultimaAbaMotivo: 'fixada' | 'mes_corrente' | 'mes_recente' | null;
  abas: string[] | null;
  mapeamentoConfirmado: boolean;
  ultimaConsultaEm: string | null;
  ultimaLeituraEm: string | null;
  ultimaMudancaEm: string | null;
  falhasSeguidas: number;
  pendenciasAbertas: number;
  /** Sem a pendência de Planilha, que mora na linha da escola (0112, FR-006). */
  pendenciasDeAluno: number;
  /** As colunas a confirmar ou a falha, aberta: a ação da linha da escola. */
  pendenciaDaPlanilha: Pendencia | null;
  /** O mapeamento em uso, campo → letra da coluna. */
  mapeamento: Record<string, string> | null;
  mapeamentoConfirmadoPor: string | null;
  mapeamentoConfirmadoEm: string | null;
  cobertura: Cobertura;
  lendoAgora: { idExecucao: number; etapa: string | null } | null;
  driveArquivoId: string;
  historico: AtoDaPlanilha[];
}

/** Os ativos da escola com cada campo da ficha, e com os quatro (0112, FR-002). */
export interface Cobertura {
  ativos: number;
  completas: number;
  endereco: number;
  bairro: number;
  telefone: number;
  responsavel: number;
}

export const CAMPOS_DA_FICHA: Array<{ chave: keyof Omit<Cobertura, 'ativos' | 'completas'>; rotulo: string }> = [
  { chave: 'endereco', rotulo: 'Endereço' }, { chave: 'bairro', rotulo: 'Bairro' },
  { chave: 'telefone', rotulo: 'Telefone' }, { chave: 'responsavel', rotulo: 'Responsável' },
];

/** Os tipos que se aceitam em lote: os de escolha única (0112, RN-01). */
export const TIPOS_DO_LOTE: TipoDePendencia[] = ['atualizar_campo', 'reativar', 'mesmo_aluno'];

/** Os que se decidem no painel ao lado da fila (0112, FR-010). */
export const TIPOS_DO_PAINEL: TipoDePendencia[] = ['mesmo_aluno', 'incluir', 'turma'];

export interface Contagens {
  linhas: number;
  ignoradas: Array<{ numero: number; motivo: string }>;
  reconhecidas: Record<string, number>;
  repetidas: number;
  camposGravados: number;
  pendenciasNovas: Partial<Record<TipoDePendencia, number>>;
  pendenciasAbertas: number;
}

export interface Execucao {
  id: number;
  origem: 'agendada' | 'pedida' | 'envio';
  pedidaPor: string | null;
  versao: string | null;
  aba: string | null;
  abaMotivo: string | null;
  situacao: 'rodando' | 'concluida' | 'falhou' | 'desistiu' | 'sem_mapeamento';
  etapa: string | null;
  contagens: Partial<Contagens>;
  erro: string | null;
  iniciadaEm: string;
  terminadaEm: string | null;
}

export interface Mudanca {
  idAluno: number;
  aluno: string | null;
  campo: string;
  antes: string | null;
  depois: string | null;
  regra: 'vazio' | 'decisao' | 'inclusao' | 'turma';
  autor: string;
  em: string;
}

export interface Candidato { idAluno: number; nome: string; nota: number; ativo: boolean }
export interface LinhaDaPlanilha { numero: number; campos: Record<string, string> }

/** O que cada tipo guarda em `dados` (leitura.service.ts). */
export interface DadosDaPendencia {
  linha?: LinhaDaPlanilha;
  candidatos?: Candidato[];
  rotulo?: string | null;
  motoristaSugerido?: number;
  coluna?: string;
  atual?: string;
  planilha?: string;
  partes?: Record<string, string> | null;
  aluno?: string;
  grafia?: string;
  original?: string;
  alunos?: number;
  vistoPelaUltimaVezEm?: string | null;
  motivo?: 'mapeamento' | 'falha';
  erro?: string;
  falhasSeguidas?: number;
  aba?: string;
  linhaDoCabecalho?: number;
  assinatura?: string;
  colunas?: Array<{ letra: string; titulo: string; tipo: string; preenchimento: number }>;
  amostra?: string[][];
  sugestao?: Record<string, string>;
  sugestaoIa?: { mapeamento: Record<string, string>; observacao: string } | null;
  anterior?: string | null;
  parecer?: ParecerDeCasamento | SugestaoDeTurma | null;
}

export interface ParecerDeCasamento { idAluno: number; confianca: 'alta' | 'media' | 'baixa'; motivo: string }
export interface SugestaoDeTurma { idTurmaExistente: number; nomeTurma: string; domSerie: string; domTurno: string; motivo: string }

export interface Pendencia {
  id: number;
  tipo: TipoDePendencia;
  idPlanilha: number;
  escola: string;
  idAluno: number | null;
  aluno: string | null;
  dados: DadosDaPendencia;
  situacao: 'aberta' | 'aceita' | 'recusada' | 'superada';
  abertaEm: string;
  vistaEm: string;
}

export interface FichaDoAluno {
  vinculos: Array<{ id: number; planilha: string; escola: string; aba: string | null; dados: Record<string, string>;
    presente: boolean; primeiraVezEm: string; ultimaVezEm: string; simade: string | null }>;
  historico: Array<{ operacao: string; campo: string; antes: string | null; depois: string | null; em: string; autor: string | null }>;
}

async function lerJson<T>(r: Response, padrao: string): Promise<T> {
  if (!r.ok) {
    const corpo = await r.json().catch(() => ({}));
    throw new Error(corpo?.error || padrao);
  }
  return r.json() as Promise<T>;
}

const comCorpo = (metodo: string) => (url: string, corpo: unknown) => authFetch(url, {
  method: metodo, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(corpo ?? {}),
});
const postar = comCorpo('POST');
const colocar = comCorpo('PUT');
const remendar = comCorpo('PATCH');

const BASE = '/api/planilhas';

export const lerPlanilhas = async () =>
  lerJson<Planilha[]>(await authFetch(BASE), 'Não foi possível ler as planilhas.');

export const lerConta = async () =>
  lerJson<{ email: string | null; ia: boolean }>(await authFetch(`${BASE}/conta`), 'Não foi possível ler a conta do Drive.');

/** Teto de segurança da leitura da fila inteira (a primeira leitura das 13 escolas passa de mil). */
export const TETO_DA_FILA = 5000;

const paginaDaFila = async (pagina: number) => lerJson<{ total: number; itens: Pendencia[] }>(
  await authFetch(`${BASE}/pendencias?porPagina=200&pagina=${pagina}`), 'Não foi possível ler as pendências.');

/**
 * Todas as pendências abertas, de 200 em 200 até o total que o servidor diz:
 * a tela pagina, filtra e conta sobre a fila inteira. Antes, uma página só de
 * 200 fazia o cabeçalho parar em 200 (0111, 25/10/2026). A primeira diz o
 * total, e as outras vêm juntas: em sequência, 1.584 eram 8 idas (0112).
 */
export async function lerPendencias(): Promise<{ total: number; itens: Pendencia[] }> {
  const primeira = await paginaDaFila(1);
  const paginas = Math.min(Math.ceil(primeira.total / 200), TETO_DA_FILA / 200);
  const resto = await Promise.all(Array.from({ length: Math.max(paginas - 1, 0) }, (_, i) => paginaDaFila(i + 2)));
  return { total: primeira.total, itens: [primeira, ...resto].flatMap((r) => r.itens) };
}

/** Os dois números do menu (0112, FR-007): as pendências de aluno e as planilhas que pedem ação. */
export const lerContador = async () =>
  lerJson<{ abertas: number; planilhas: number }>(await authFetch(`${BASE}/pendencias/contador`), '');

export const acompanhar = async (link: string, idEscola: number) =>
  lerJson<{ id: number; titulo: string }>(await postar(BASE, { link, idEscola }), 'Não foi possível acompanhar a planilha.');

export const alterarPlanilha = async (id: number, mudanca: { situacao?: string; abaFixada?: string | null }) =>
  lerJson<{ ok: true }>(await remendar(`${BASE}/${id}`, mudanca), 'Não foi possível alterar a planilha.');

export const trocarLink = async (id: number, link: string) =>
  lerJson<{ titulo: string }>(await colocar(`${BASE}/${id}/link`, { link }), 'Não foi possível trocar o link.');

export const confirmarMapeamento = async (id: number, mapeamento: Record<string, string>, assinatura: string, semIa = false) =>
  lerJson<{ ok: true }>(await colocar(`${BASE}/${id}/mapeamento`, { mapeamento, assinatura, ...(semIa ? { semIa: true } : {}) }),
    'Não foi possível confirmar o mapeamento.');

export const lerAgora = async (id: number) =>
  lerJson<{ idExecucao: number; situacao: string; contagens?: Contagens; erro?: string }>(
    await postar(`${BASE}/${id}/ler`, {}), 'Não foi possível ler a planilha.');

export async function enviarArquivo(id: number, arquivo: File) {
  const corpo = new FormData();
  corpo.append('arquivo', arquivo, arquivo.name);
  return lerJson<{ idExecucao: number; situacao: string; contagens?: Contagens; erro?: string }>(
    await authFetch(`${BASE}/${id}/envio`, { method: 'POST', body: corpo }), 'Não foi possível ler o arquivo.');
}

export const lerExecucoes = async (id: number) =>
  lerJson<{ total: number; itens: Execucao[] }>(await authFetch(`${BASE}/${id}/execucoes?porPagina=50`), 'Não foi possível ler as execuções.');

export const lerMudancas = async (idExecucao: number) =>
  lerJson<Mudanca[]>(await authFetch(`${BASE}/execucoes/${idExecucao}/mudancas`), 'Não foi possível ler as mudanças.');

export const decidir = async (id: number, acao: 'aceitar' | 'recusar', escolha: Record<string, unknown> = {}, motivo?: string) =>
  lerJson<{ ok: true }>(await postar(`${BASE}/pendencias/${id}/decisao`, { acao, escolha, motivo }), 'Não foi possível decidir.');

/** O recorte do lote (0112, FR-012): o tipo, a planilha e, no Atualizar campo, a coluna. */
export interface FiltroDoLote { tipo: TipoDePendencia; idPlanilha: number | null; coluna: string | null }

export interface SimulacaoDoLote {
  total: number;
  alunos: number;
  porColuna: Record<string, number>;
  trocasDeTurno: number;
  exemplos: Array<{ id: number; escola: string; aluno: string | null; coluna: string | null; atual: string | null; planilha: string | null }>;
  assinatura: string;
  /** Só no Mesmo aluno? de confiança alta (FR-018): os pares que entram e o que fica de fora. */
  pares?: ParDoLote[];
  consultadas?: number;
  foraDoLote?: number;
  semParecer?: number;
}

export interface ParDoLote {
  id: number;
  escola: string;
  linha: number | null;
  nomeNaPlanilha: string | null;
  idAluno: number;
  nomeNoCadastro: string | null;
  motivo: string | null;
}

export const simularLote = async (f: FiltroDoLote) =>
  lerJson<SimulacaoDoLote>(await postar(`${BASE}/pendencias/decisao/filtro`, { ...f, simular: true }), 'Não foi possível simular o lote.');

export const aceitarLote = async (f: FiltroDoLote, assinatura: string, motivo?: string) =>
  lerJson<{ aceitas: number[]; recusadas: Array<{ id: number; motivo: string }> }>(
    await postar(`${BASE}/pendencias/decisao/filtro`, { ...f, assinatura, motivo }), 'Não foi possível aceitar o lote.');

export const pedirParecer = async (id: number) =>
  (await lerJson<{ parecer: unknown }>(await postar(`${BASE}/pendencias/${id}/parecer`, {}), 'Não foi possível pedir o parecer.')).parecer;

export const lerFichaDoAluno = async (idAluno: number) =>
  lerJson<FichaDoAluno>(await authFetch(`${BASE}/alunos/${idAluno}`), 'Não foi possível ler a planilha do aluno.');

/** Motoristas ativos, para a inclusão (a mesma rota do cadastro de alunos). */
export async function lerMotoristas(): Promise<Array<{ id: number; nome: string }>> {
  const lista = await lerJson<Array<{ idMotorista: number; nomeMotorista: string; inativo?: boolean }>>(
    await authFetch('/api/transportador/motoristas'), 'Não foi possível ler os motoristas.');
  return lista.filter((m) => !m.inativo).map((m) => ({ id: Number(m.idMotorista), nome: m.nomeMotorista }));
}

/** As turmas de uma escola, para ligar a grafia a uma existente. */
export const lerTurmas = async (idEscola: number) =>
  lerJson<Array<{ idTurma: number; nomeTurma: string; domSerie: string | null; domTurno: string | null }>>(
    await authFetch(`/api/transportador/turmas?idEscola=${idEscola}`), 'Não foi possível ler as turmas.');

export async function lerEscolas(): Promise<Array<{ idEscola: number; nome: string; sigla: string }>> {
  const r = await authFetch('/api/escola');
  const corpo = await lerJson<unknown>(r, 'Não foi possível ler as escolas.');
  const lista = Array.isArray(corpo) ? corpo : (corpo as { data?: unknown[] })?.data ?? [];
  return (lista as Array<Record<string, unknown>>).map((e) => ({
    idEscola: Number(e.idEscola), nome: String(e.nomeEscola ?? ''), sigla: String(e.siglaEscola ?? ''),
  }));
}
